import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { TeacherApplicationForm, getInitialFormData } from '../model/TeacherApplicationForm';
import {
  validateEmail,
  validateItalianPhone,
  validateProvince,
  validateCity,
  validateStreet,
  validateZip,
  validateHasCar,
  validateQualifications,
  validateEmployment,
  validateSubjects,
  validateAvailability,
} from '../utils/teacherApplicationValidators';
import { Province, City } from '../model/TeacherInfo';
import PersonalInfoStep from '../components/teacher-application/PersonalInfoStep';
import ProfessionalInfoStep from '../components/teacher-application/ProfessionalInfoStep';
import SubjectsSelectionStep from '../components/teacher-application/SubjectsSelectionStep';
import AvailabilityStep from '../components/teacher-application/AvailabilityStep';
import SummaryStep from '../components/teacher-application/SummaryStep';
import VerticalStepper from '../components/teacher-application/VerticalStepper';
import { useLoading } from '../context/LoadingContext';
import { useError } from '../context/ErrorContext';
import api from '../utils/api';
import logoFPC from '../ui/logoFPC.svg';

// Step titles and subtitles
const stepInfo = [
  { title: 'Dati Personali', subtitle: 'Inserisci i tuoi dati per iniziare la candidatura' },
  { title: 'Informazioni Professionali', subtitle: 'Raccontaci della tua esperienza e qualifiche' },
  { title: 'Materie e Scuole', subtitle: 'Seleziona le materie che desideri insegnare' },
  { title: 'Disponibilità Settimanale', subtitle: 'Indica quando sei disponibile per le lezioni' },
  { title: 'Riepilogo', subtitle: 'Controlla i dati inseriti prima di inviare' },
];

// Framer Motion variants for slide animation
const slideVariants = {
  enter: (direction: number) => ({
    x: direction > 0 ? 300 : -300,
    opacity: 0,
  }),
  center: {
    x: 0,
    opacity: 1,
  },
  exit: (direction: number) => ({
    x: direction < 0 ? 300 : -300,
    opacity: 0,
  }),
};

const TeacherApplication: React.FC = () => {
  const [currentStep, setCurrentStep] = useState(1);
  const [direction, setDirection] = useState(0);
  const [formData, setFormData] = useState<TeacherApplicationForm>(getInitialFormData());
  const [errors, setErrors] = useState<{ [key: string]: string }>({});
  const [provinces, setProvinces] = useState<Province[]>([]);
  const [cities, setCities] = useState<City[]>([]);
  const { setLoading } = useLoading();
  const { setError } = useError();
  const navigate = useNavigate();

  // Load provinces on mount
  useEffect(() => {
    api.get('/province?page_size=200').then((res) => {
      setProvinces(res.data.provinces);
    });
  }, []);

  // Load cities when province changes
  useEffect(() => {
    if (formData.province_id) {
      api.get(`/city?province_id=${formData.province_id}&page_size=200`).then((res) => {
        setCities(res.data.cities);
      });
    } else {
      setCities([]);
    }
  }, [formData.province_id]);

  /**
   * Validate Personal Info (Step 1)
   */
  const validatePersonalInfo = (): boolean => {
    const newErrors: { [key: string]: string } = {};

    newErrors.first_name = !formData.first_name.trim() ? 'Il nome è obbligatorio' : '';
    newErrors.last_name = !formData.last_name.trim() ? 'Il cognome è obbligatorio' : '';
    newErrors.email = validateEmail(formData.email);
    newErrors.phone_number = validateItalianPhone(formData.phone_number);
    newErrors.province_id = validateProvince(formData.province_id);
    newErrors.city_id = validateCity(formData.city_id);
    newErrors.street_name = validateStreet(formData.street_name);
    newErrors.zip = validateZip(formData.zip);

    setErrors(newErrors);

    return Object.values(newErrors).every((error) => error === '');
  };

  /**
   * Validate Professional Info (Step 2)
   */
  const validateProfessionalInfo = (): boolean => {
    const newErrors: { [key: string]: string } = {};

    newErrors.has_car = validateHasCar(formData.has_car);
    newErrors.qualifications = validateQualifications(formData.qualifications);
    newErrors.current_employment = validateEmployment(formData.current_employment);

    setErrors(newErrors);

    return Object.values(newErrors).every((error) => error === '');
  };

  /**
   * Validate Subjects Selection (Step 3)
   */
  const validateSubjectsSelection = (): boolean => {
    const newErrors: { [key: string]: string } = {};

    newErrors.subjects = validateSubjects(formData.subjects);

    setErrors(newErrors);

    return Object.values(newErrors).every((error) => error === '');
  };

  /**
   * Validate Weekly Availability (Step 4)
   */
  const validateWeeklyAvailability = (): boolean => {
    const newErrors: { [key: string]: string } = {};

    newErrors.availability = validateAvailability(formData);

    setErrors(newErrors);

    return Object.values(newErrors).every((error) => error === '');
  };

  /**
   * Validate current step before proceeding
   */
  const validateCurrentStep = (): boolean => {
    switch (currentStep) {
      case 1:
        return validatePersonalInfo();
      case 2:
        return validateProfessionalInfo();
      case 3:
        return validateSubjectsSelection();
      case 4:
        return validateWeeklyAvailability();
      case 5:
        return true; // Summary step doesn't need validation
      default:
        return true;
    }
  };

  /**
   * Handle next button click
   */
  const handleNext = () => {
    if (validateCurrentStep()) {
      setDirection(1);
      setCurrentStep((prev) => Math.min(prev + 1, 5));
      window.scrollTo({ top: 0, behavior: 'smooth' });
    }
  };

  /**
   * Handle back button click
   */
  const handleBack = () => {
    setDirection(-1);
    setCurrentStep((prev) => Math.max(prev - 1, 1));
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  /**
   * Handle form submission
   */
  const handleSubmit = async () => {
    setLoading(true);
    try {
      // Costruisci il payload con address nidificato
      const { province_id, city_id, street_name, street_number, zip, ...rest } = formData;

      const payload = {
        ...rest,
        address: {
          street_name,
          city_id,
          province_id,
          street_number,
          zip,
          country: 'IT'
        }
      };

      await api.post('/teacher-application', payload);

      // Success - show confirmation message or redirect
      alert('Candidatura inviata con successo! Ti contatteremo presto.');
      navigate('/');
    } catch (error: any) {
      console.error('Error submitting teacher application:', error);
      setError(
        true,
        error.response?.data?.message || 'Errore durante l\'invio della candidatura. Riprova più tardi.',
        error.response?.status
      );
    } finally {
      setLoading(false);
    }
  };

  const renderStep = () => {
    switch (currentStep) {
      case 1:
        return (
          <PersonalInfoStep
            formData={formData}
            setFormData={setFormData}
            errors={errors}
            setErrors={setErrors}
            provinces={provinces}
            cities={cities}
          />
        );
      case 2:
        return (
          <ProfessionalInfoStep
            formData={formData}
            setFormData={setFormData}
            errors={errors}
            setErrors={setErrors}
          />
        );
      case 3:
        return (
          <SubjectsSelectionStep
            formData={formData}
            setFormData={setFormData}
            errors={errors}
            setErrors={setErrors}
          />
        );
      case 4:
        return (
          <AvailabilityStep
            formData={formData}
            setFormData={setFormData}
            errors={errors}
            setErrors={setErrors}
          />
        );
      case 5:
        return (
          <SummaryStep
            formData={formData}
            setFormData={setFormData}
            setCurrentStep={setCurrentStep}
            errors={errors}
            setErrors={setErrors}
            onSubmit={handleSubmit}
            provinces={provinces}
            cities={cities}
          />
        );
      default:
        return null;
    }
  };

  return (
    <div className="flex items-center justify-center p-2 w-full max-w-5xl">
      <div className="w-full bg-white rounded-2xl shadow-lg flex flex-col md:flex-row overflow-hidden">
        {/* Sidebar - Desktop: vertical left, Mobile: horizontal top */}
        <aside className="w-full md:w-64 bg-fpcpink p-4 md:p-6 flex flex-col items-center md:items-start">
          {/* Logo */}
          <img src={logoFPC} alt="Formando Percorsi" className="h-8 md:h-10 mb-4 md:mb-8" />

          {/* Stepper */}
          <VerticalStepper currentStep={currentStep} />
        </aside>

        {/* Main Content */}
        <main className="flex-1 p-4 md:p-6 lg:p-8 flex flex-col">
          {/* Title and Subtitle */}
          <div className="mb-6">
            <h1 className="text-2xl md:text-3xl font-bold text-gray-800">
              {stepInfo[currentStep - 1].title}
            </h1>
            <p className="text-gray-600 mt-1">
              {stepInfo[currentStep - 1].subtitle}
            </p>
          </div>

          {/* Step Content with Animation */}
          <div className="flex-1 overflow-hidden">
            <AnimatePresence mode="wait" custom={direction}>
              <motion.div
                key={currentStep}
                custom={direction}
                variants={slideVariants}
                initial="enter"
                animate="center"
                exit="exit"
                transition={{
                  x: { type: 'spring', stiffness: 300, damping: 30 },
                  opacity: { duration: 0.2 },
                }}
              >
                {renderStep()}
              </motion.div>
            </AnimatePresence>
          </div>

          {/* Navigation Buttons */}
          {currentStep < 5 && (
            <div className="flex justify-between mt-2 pt-6 border-t border-gray-200">
              <button
                type="button"
                onClick={handleBack}
                disabled={currentStep === 1}
                className={`py-3 px-6 rounded-lg font-medium transition-all ${
                  currentStep === 1
                    ? 'bg-gray-100 text-gray-400 cursor-not-allowed'
                    : 'bg-gray-200 text-gray-700 hover:bg-gray-300'
                }`}
              >
                Indietro
              </button>

              <button
                type="button"
                onClick={handleNext}
                className="py-3 px-8 rounded-lg font-medium bg-fpcred text-white hover:bg-red-700 transition-all shadow-md hover:shadow-lg"
              >
                Avanti
              </button>
            </div>
          )}
        </main>
      </div>
    </div>
  );
};

export default TeacherApplication;
